/* eslint-disable no-underscore-dangle */
import { RadioGroup } from '@mui/material';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import Button from '../../../generic/Button';
import Flex from '../../../generic/Flex';
import {
  FormGroup,
  LabeledPasswordInput,
  LabeledRadio,
  LabeledTextArea,
  LabeledTextInput,
} from '../../../generic/Forms';
import type { NpmRegistry, NpmRegistryAuth } from '../../../../../typings/auth';

import css from '../../../generic/Forms/Radio.module.less';

type AuthMethod = 'show-token-field' | 'hide-token-field';

type RegistryProps = {
  registry: NpmRegistry;
  onRemoveRegistry: () => void;
  onUpdateRegistry: <K extends keyof NpmRegistry>(member: K, value: NpmRegistry[K]) => void;
  onUpdateRegistryAuth: <K extends keyof NpmRegistryAuth>(member: K, value: NpmRegistryAuth[K]) => void;
  onSetShowTokenField: (value: AuthMethod) => void;
};

const Registry: VoidFunctionComponent<RegistryProps> = ({
  registry,
  onRemoveRegistry,
  onUpdateRegistry,
  onUpdateRegistryAuth,
  onSetShowTokenField,
}) => {
  const selectedRadioButton = registry._showTokenField ? 'show-token-field' : 'hide-token-field';
  return (
    <div className="row card" key={registry._id} style={{ marginRight: 0, marginLeft: 0 }}>
      <Flex justify="space-between" align="flex-start">
        <div className="margin-bottom-md">
          <RadioGroup
            name="show-token-field-radio-buttons"
            className={`${css.radios} ${css['radios-horizontal']}`}
            style={{ display: 'flex', flexDirection: 'row' }}
            value={selectedRadioButton}
            onChange={event => onSetShowTokenField(event.target.value as AuthMethod)}
          >
            <LabeledRadio
              value="hide-token-field"
              label="Username/Password"
            />
            <LabeledRadio
              value="show-token-field"
              label="Token"
            />
          </RadioGroup>
        </div>
        <Button
          size="small"
          type="error"
          onClick={onRemoveRegistry}
        >
          Remove
        </Button>
      </Flex>
      <Flex direction="column" gap="12px">
        <FormGroup direction="row" spacing="sm">
          <LabeledTextInput
            label="URL"
            value={registry.url || ''}
            onChange={value => onUpdateRegistry('url', value)}
            placeholder="https://your-registry.npmjs.org"
            size="small"
          />
          {registry._showTokenField ? (
            <LabeledPasswordInput
              label="Token"
              value={registry.auth?.hasToken ? '••••••••' : registry.auth?.token || ''}
              onChange={value => onUpdateRegistryAuth('token', value)}
              size="small"
            />
          ) : (
            <LabeledTextInput
              type="email"
              size="small"
              label="Email"
              value={registry.auth?.email || ''}
              onChange={value => onUpdateRegistryAuth('email', value)}
              placeholder={window.user?.email || 'npm@domain.com'}
            />
          )}
        </FormGroup>
        {!registry._showTokenField && (
          <FormGroup direction="row" spacing="sm">
            <LabeledTextInput
              size="small"
              label="Username"
              value={registry.auth?.username || ''}
              onChange={value => onUpdateRegistryAuth('username', value)}
              placeholder="npmuser"
            />
            <LabeledPasswordInput
              size="small"
              label="Password"
              value={registry.auth?.hasPassword ? '••••••••' : registry.auth?.password?.value || ''}
              onChange={value => onUpdateRegistryAuth('password', { __type: 's', value })}
            />
          </FormGroup>
        )}
        <FormGroup>
          <LabeledTextArea
            size="small"
            label="CA Certificate (Optional)"
            value={registry.hasCa ? '••••••••' : registry.ca?.value || ''}
            onChange={value => onUpdateRegistry('ca', { __type: 's', value })}
          />
        </FormGroup>
      </Flex>
    </div>
  );
};

export default Registry;
